import React from "react";
import cx from "classnames";
import { css } from "@linaria/core";
import { colors, fonts, spacings } from "../../Theme";
import ErrorWarningLine from "../../IconV2/react/ErrorWarningLine";
import RevertButton from "../../Input/RevertButton";
import { InformationLine } from "../../IconV2/react";
import TooltipV2 from "../../TooltipV2";
import Text from "../../Text";
import t from "../../i18n";

export interface Props {
  className?: string;
  inputContainerClassName?: string;
  style?: React.CSSProperties;
  id: string;
  label?: string;
  hint?: string;
  error?: React.ReactNode;
  displayErrorMessage?: boolean;
  disabled?: boolean;
  required?: boolean;
  revertable?: boolean;
  focused?: boolean;
  revert?: () => void;
  beforeInputSlot?: React.ReactNode;
  afterInputSlot?: React.ReactNode;
  tooltipOverlay?: React.ReactNode;
}

const field = css`
  column-gap: ${spacings.xxs};
  display: inline-grid;
  grid-template-columns: min-content 1fr;
  grid-template-rows: min-content;
  grid-auto-columns: min-content;
  grid-auto-rows: min-content;
  font-family: "Open Sans", sans-serif;
  row-gap: ${spacings.xxs};
`;

const fieldLabel = css`
  color: ${colors.carbon800};
  font-size: 14px;
  font-weight: 600;
  grid-column: 1 / span 1;
  grid-row: 1 / span 1;
  white-space: nowrap;
  display: flex;

  .${field}.disabled & {
    opacity: 0.5;
  }
`;

const labelFieldSup = css`
  color: ${colors.darkNitrogen600};
  vertical-align: top;
`;

const fieldRevertButton = css`
  grid-column: 2 / span 1;
  grid-row: 1 / span 1;

  &:not(.visible),
  .${field}.disabled & {
    visibility: hidden;
    pointer-events: none;
  }
`;

const fieldHint = css`
  color: ${colors.carbon700};
  grid-column: 1 / span 2;

  .${field}.disabled & {
    opacity: 0.5;
  }
`;

const fieldError = css`
  color: ${colors.oxygen400};
  font: ${fonts.body2};
  grid-column: 1 / span 2;
  grid-row: 3 / span 1;
  text-align: left;
  display: flex;
  gap: 4px;
  align-items: flex-start;

  .${fieldHint} ~ & {
    grid-row: 4 / span 1;
  }

  > :first-child {
    flex-shrink: 0;
    height: 18px;
  }
`;

const fieldInput = css`
  align-items: center;
  background-color: ${colors.hydrogen};
  box-sizing: border-box;
  border: 1px solid;
  border-color: ${colors.titanium200};
  border-radius: 4px;
  color: ${colors.carbon900};
  display: flex;
  grid-column: 1 / span 2;
  grid-row: span 1;
  overflow: clip;
  padding-left: ${spacings.s};
  position: relative;
  width: var(--input-container-width, 100%);

  &:has(input[type="search"]) {
    padding-left: 12px;
  }

  &:has(select) {
    padding-left: 0;
  }

  &:not(.disabled):not(.error):not(:focus-within):not([data-focused="true"]):hover {
    border-color: ${colors.titanium300};
  }

  &:focus-within:not(.error),
  &[data-focused="true"] {
    border-color: ${colors.lightNitrogen500_Main};
  }

  &.error {
    color: ${colors.carbon900};
    border-color: ${colors.oxygen400};
  }

  &.disabled {
    background-color: ${colors.titanium50};

    user-select: none;
    cursor: not-allowed;

    > * {
      pointer-events: none;
    }
  }

  > :not(input) {
    flex-shrink: 0;
  }

  &:has(input[type="search"]) > :not(input, select, input):first-child {
    margin-right: ${spacings.xs};
  }
`;

const fieldTooltip = css`
  max-width: 350px;
`;

const fieldTooltipAnchor = css`
  margin-left: ${spacings.xxs};
  svg {
    transform: translateY(2px);
  }
`;

// error
// errorBoolean
const Field = ({
  id,
  label,
  hint,
  error,
  displayErrorMessage = true,
  required = false,
  disabled = false,
  revertable = false,
  focused = false,
  revert,
  beforeInputSlot,
  afterInputSlot,
  className,
  inputContainerClassName,
  children,
  style,
  tooltipOverlay,
}: React.PropsWithChildren<Props>) => {
  const hasLabel = React.useMemo(() => typeof label === "string" && label.length > 0, [label]);
  const hasError = React.useMemo(() => Boolean(error), [error]);

  return (
    <div
      className={cx([
        field,
        className,
        {
          required,
          labelled: hasLabel,
          disabled,
        },
        "Field",
      ])}
    >
      {hasLabel && (
        <>
          <label className={fieldLabel} htmlFor={id}>
            {label}
            {required && <sup className={labelFieldSup}>*</sup>}
            {tooltipOverlay && (
              <TooltipV2 overlay={tooltipOverlay} className={fieldTooltip}>
                <span className={fieldTooltipAnchor}>
                  <InformationLine />
                </span>
              </TooltipV2>
            )}
          </label>
          {hint && (
            <Text type="body2" className={fieldHint}>
              {hint}
            </Text>
          )}
          {revertable && (
            <RevertButton
              className={cx(fieldRevertButton, { visible: revertable })}
              onClick={revert}
              text={t("input.revert")}
            />
          )}
        </>
      )}
      <div
        className={cx(
          fieldInput,
          {
            error: hasError,
            disabled,
          },
          inputContainerClassName,
        )}
        style={style}
        data-focused={focused}
      >
        {beforeInputSlot}
        {children}
        {afterInputSlot}
      </div>
      {hasError && displayErrorMessage && (
        <p className={fieldError}>
          <ErrorWarningLine size="s" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
};

export default React.memo(Field);
